import { Form, FormValues } from 'antd';
import { format, isValid, parse } from 'date-fns';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { parseServerDateFormat } from '../utils/format-date/format-date';
import { NamePath } from 'antd/es/form/interface';

type FieldValue = string | Date | FieldValue[];

const formatFieldValue = (value: FieldValue, dateFormat = 'yyyy-MM-dd'): string => {
  if (Array.isArray(value)) {
    return value.map(valueItem => formatFieldValue(valueItem)).join();
  }
  if (value instanceof Date) {
    return format(value, parseServerDateFormat(dateFormat));
  }

  return value;
};

const parseFieldValue = (value: string, dateFormat = 'yyyy-MM-dd'): FieldValue => {
  if (value.includes(',')) {
    return value.split(',').map(valueItem => parseFieldValue(valueItem));
  }

  const dateValue = parse(value, parseServerDateFormat(dateFormat), new Date());
  if (isValid(dateValue)) {
    return dateValue;
  }

  return value;
};

const useFormWithSearchParams = <T extends object>(initialValues?: FormValues<T>, format?: string) => {
  const [form] = Form.useForm<T>();
  const [searchParams, setSearchParams] = useSearchParams();

  const setSearchParamsFromForm = () => {
    const fields = Object.fromEntries(
      Object.entries(form.getFieldsValue()).map(([key, value]) => [key, formatFieldValue(value, format)]),
    );
    setSearchParams(existingSearchParams => {
      Object.entries(fields).forEach(([key, value]) => {
        if (value) {
          existingSearchParams.set(key, value);
        } else {
          existingSearchParams.delete(key);
        }
      });

      return existingSearchParams;
    });
  };

  useEffect(() => {
    const fields = Object.keys(form.getFieldsValue());
    form.setFields(
      fields.map(field => {
        return {
          name: [field] as NamePath,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          value: parseFieldValue(searchParams.get(field) || '', format) || (initialValues as any)?.[field] || '',
          validating: false,
        };
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, form]);

  useEffect(() => {
    setSearchParamsFromForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    ...form,
    resetFields: (...args: Parameters<typeof form.resetFields>) => {
      form.resetFields.call(args);

      setSearchParamsFromForm();
    },
    submit: () => {
      const fields = Object.fromEntries(
        Object.entries(form.getFieldsValue()).map(([key, value]) => [key, formatFieldValue(value, format)]),
      );

      setSearchParams(existingSearchParams => {
        Object.entries(fields).forEach(([key, value]) => {
          if (value) {
            existingSearchParams.set(key, value);
          } else {
            existingSearchParams.delete(key);
          }
        });

        if (existingSearchParams.has('page')) {
          existingSearchParams.set('page', '1');
        }

        return existingSearchParams;
      });

      form.submit();
    },
  };
};

export default useFormWithSearchParams;
